import React, { useEffect, useState } from 'react';
import { defineMessages } from 'react-intl';
import { StepType } from '../../../services/getStepOrder';
import Step from '../Step/Step';
import JumpToEnd, { PendingJumpType } from '../JumpToEnd/JumpToEnd';
import globalMessages from 'src/services/globalMessages';
import CalendarSelect from '../modules/CalendarSelect/CalendarSelect';
import MockModule from 'src/test-mocks/MockModule';
import ServiceSelect from '../modules/ServiceSelect/ServiceSelect';
import DateTimeSelectStep from '../modules/DateTimeSelect/DateTimeSelectStep';
import CustomerDataStep from '../modules/CustomerData/CustomerDataStep';
import TimeTableSelect from '../modules/TimeTableSelect/TimeTableSelect';
import { ReservationOrder, ICompany } from 'src/entities/company';
import { IStepModuleProps } from 'src/models/modules';
import { useLocation, useNavigate } from 'react-router-dom';
import { Routes } from 'src/routes';
import { useAppContext } from 'src/contexts/AppContext';
import { useAutoRefresh } from 'src/hooks';

const stepNames = defineMessages({
    [StepType.Calendar]: {
        id: 'calendar',
        defaultMessage: 'Naptár',
    },
    [StepType.Timetable]: {
        id: 'timetable',
        defaultMessage: 'Órarend',
    },
    [StepType.Service]: {
        id: 'service',
        defaultMessage: 'Szolgáltatás',
    },
    [StepType.Date]: globalMessages.dateTime,
    [StepType.CustomerData]: globalMessages[StepType.CustomerData],
    [StepType.ReservationEnd]: globalMessages[StepType.ReservationEnd],
});

type CompletedStep = { [step in StepType]?: boolean };

export interface IStepperProps {
    stepOrder: StepType[];
    pendingLength: number;
    isActive: boolean;
    shouldHideInitialProgressBar?: boolean;
    onChange: (activeStepIndex: number) => void;
    activeStepIndex: number;
    removeReservation: () => void;
}

const Stepper: React.FC<IStepperProps> = ({
    activeStepIndex,
    stepOrder,
    pendingLength,
    isActive,
    shouldHideInitialProgressBar,
    onChange,
    removeReservation,
}) => {
    useAutoRefresh();

    const location = useLocation();
    const navigate = useNavigate();
    const { company: companyFromContext } = useAppContext();
    const [completedSteps, setCompletedSteps] = useState<CompletedStep>({});
    const [hasProgressed, setHasProgressed] = useState(false);
    const lastIndex = stepOrder.length - 1;

    useEffect(() => {
        if (!hasProgressed && activeStepIndex > 0) {
            setHasProgressed(true);
        }

        if (activeStepIndex && !Object.keys(completedSteps).length) {
            const steps: CompletedStep = {};
            stepOrder.forEach((step, index) => {
                if (index !== stepOrder.length - 1) {
                    steps[step] = true;
                }
            });
            setCompletedSteps({ ...steps });
        }
    }, [activeStepIndex, stepOrder, completedSteps, hasProgressed]);

    const getStepByIndex = (index: number): StepType => stepOrder[index];
    const getStepIsCompleted = (index: number): boolean => !!completedSteps[getStepByIndex(index)];
    const getAllStepIsCompleted = (): boolean => {
        const steps = [...stepOrder];
        steps.pop();
        return steps.every((step) => completedSteps[step]);
    };
    const getStepsAfterModify = (modifiedStepIndex: number): CompletedStep => {
        return Object.keys(completedSteps).reduce((steps: CompletedStep, currentStep: StepType) => {
            if (modifiedStepIndex < stepOrder.findIndex((step) => step === currentStep)) {
                steps[currentStep] = false;
            }
            return steps;
        }, {});
    };
    const nextStep = () => {
        let stepsToReset: CompletedStep = {};

        if (getStepIsCompleted(activeStepIndex)) {
            stepsToReset = getStepsAfterModify(activeStepIndex);
        }

        setCompletedSteps({
            ...completedSteps,
            [stepOrder[activeStepIndex]]: true,
            ...stepsToReset,
        });

        if (activeStepIndex + 1 === stepOrder.length - 1) {
            navigate({
                pathname: Routes.ReservationEnd,
                search: location.search,
            });
        }

        onChange(activeStepIndex + 1);
    };
    const modifyStep = (stepIndex: number) => {
        onChange(stepIndex);
    };
    const jumpToEnd = () => {
        if (!getAllStepIsCompleted()) {
            removeReservation();
        } else {
            onChange(stepOrder.length - 1);
        }

        navigate({
            pathname: Routes.ReservationEnd,
            search: location.search,
        });
    };
    const getStepComponent = (
        step: StepType,
        isFirstStep: boolean,
        company: ICompany | null
    ): React.FC<IStepModuleProps> => {
        switch (step) {
            case StepType.Calendar:
                return (props) => (
                    <CalendarSelect
                        {...props}
                        shouldGetByService={!isFirstStep}
                        hasRandomCalendar={
                            company?.ReservationOrder ===
                            ReservationOrder.ServiceFirstWithAllCalendarOption
                        }
                        canUserSelectCalendar={company?.CanUserSelectCalendar}
                    />
                );

            case StepType.Timetable:
                return (props) => (
                    <TimeTableSelect
                        {...props}
                        isPaymentEnabled={company?.PaymentSettings.IsPaymentEnabled}
                    />
                );
            case StepType.Service:
                return (props) => (
                    <ServiceSelect
                        {...props}
                        shouldGetByCalendar={!isFirstStep}
                        isPaymentEnabled={company?.PaymentSettings.IsPaymentEnabled}
                    />
                );
            case StepType.Date:
                return (props) => <DateTimeSelectStep {...props} />;
            case StepType.CustomerData:
                return (props) => <CustomerDataStep {...props} />;
            case StepType.ReservationEnd:
                return (props) => <MockModule {...props} name={StepType.ReservationEnd} />;
        }
    };

    if (!isActive) {
        return null;
    }
    return (
        <div className="stepper">
            {pendingLength > 1 && (
                <JumpToEnd
                    type={PendingJumpType.Top}
                    action={jumpToEnd}
                    reservationCount={pendingLength - 1}
                />
            )}
            {stepOrder.map((step: StepType, i: number) => (
                <Step
                    key={`step-${step}`}
                    isProgressBarHidden={!hasProgressed && shouldHideInitialProgressBar}
                    isActive={i === activeStepIndex}
                    isLast={i === lastIndex}
                    isCompleted={getStepIsCompleted(i)}
                    content={getStepComponent(step, i === 0, companyFromContext)}
                    name={stepNames[step]}
                    step={step}
                    stepIndex={i}
                    nextHandler={nextStep}
                    modifyHandler={modifyStep}
                    jumpToEndHandler={jumpToEnd}
                />
            ))}
        </div>
    );
};

export default Stepper;
